/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect, useCallback } from 'react'
import { Box, Divider, FormControlLabel, Switch, Typography } from '@mui/material'
import GenericModal from '../../../../components/GenericModal'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { useAppDispatch } from '../../../../store'
import { type ISurveySection, setfilters } from '../../../../store/slices/SurveyTemplateSlice'
import { getSurveySections } from '../../../../store/slices/SurveyTemplateSlice/selectors'
import { FormProvider, type SubmitHandler, useForm, Controller } from 'react-hook-form'
import { grey } from '../../../../colors'
import { type IAttribute } from '../../../../models/Attributes'
import AttributesFilter from './AttributesFilter'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Branching from './Branching'
import { BranchingOperation, SurveyItemQuestionType } from '../../types'
import { BranchingOperationsDropdown } from '../../constant'
import { findParentSectionAndQuestion } from '../../helpers'
import Checkbox from '@mui/material/Checkbox'
import { ReactComponent as SelectedCheckBox } from '../../../../assets/check-box-selected.svg'

const icon = <Box border={`1px solid ${grey[350]}`} borderRadius="4px" width="18px" height="18px" />

interface SectionFiltersModalProps {
  section: ISurveySection
  isFilterModalOpen: boolean
  closeFilterModal: () => void
}

export interface ICourseFilters {
  key: IAttribute
  op: string
  value: string
}
interface FormData {
  courseFilters: ICourseFilters[]
  isRepeatPerInstructor: boolean
  isSelectInstructorEnabled: boolean
  branchingFilters?: any[]
}

export const SectionFiltersModal: React.FC<SectionFiltersModalProps> = ({ section, isFilterModalOpen, closeFilterModal }) => {
  const sectionsList = getSurveySections()
  const { parentSection, parentQuestion } = findParentSectionAndQuestion(sectionsList, section) ?? {}
  const dispatch = useAppDispatch()
  const validationSchema = yup.object().shape({
    ...(section?.parentSurveySectionId
      ? {
          branchingFilters: yup.array().of(
            yup.object().shape({
              operation: yup.object().shape({
                value: yup.string().required('Operation is required')
              }).required('Operation is required'),
              answers: yup.array().when('operation.value', {
                is: (operationValue: string) => operationValue === BranchingOperation.Is,
                then: schema => schema
                  .min(1, 'At least one answer is required when operation is "Is"')
                  .required('Answers are required for the "Is" operation'),
                otherwise: schema => schema.notRequired().nullable()
              })
            })
          )
        }
      : {
          courseFilters: yup.array().of(
            yup.object().shape({
              key: yup.object().required('Attribute is required'),
              op: yup.string().required('Operation is required'),
              value: yup.array().required('Value is required')
            })
          ),
          isRepeatPerInstructor: yup.boolean()
        })
  })

  const methods = useForm<any>({
    defaultValues: {
      isRepeatPerInstructor: section?.isRepeatPerInstructor ?? false,
      isSelectInstructorEnabled: section?.isSelectInstructorEnabled ?? false,
      courseFilters: [],
      branchingFilters: [
        {
          sectionTitle: section?.title ?? '',
          questionPrompt: section?.branchingFilters?.prompt ?? '',
          operation: BranchingOperationsDropdown.find((b) => b.value === section?.branchingFilters?.operation) ?? null,
          answers: section?.branchingFilters?.answer ?? []
        }
      ]
    },
    resolver: yupResolver(validationSchema)
  })

  const { handleSubmit, control, reset, watch, setValue, formState: { isDirty, errors } } = methods

  const isRepeatPerInstructor = watch('isRepeatPerInstructor')

  useEffect(() => {
    if (!isRepeatPerInstructor) {
      setValue('isSelectInstructorEnabled', false)
    }
  }, [isRepeatPerInstructor, setValue])

  const onSubmit: SubmitHandler<FormData> = useCallback(async (values) => {
    const preparedAttributes = values?.courseFilters?.map((attribute) => ({
      op: attribute.op,
      values: attribute?.value,
      type: attribute.key?.attributeType,
      key: attribute.key?.attributeKey
    }))

    const branchingFilters = values?.branchingFilters?.map((_branchingFilter) => ({
      operation: _branchingFilter?.operation?.value,
      prompt: _branchingFilter?.questionPrompt,
      answer: _branchingFilter?.itemType === SurveyItemQuestionType.LINEAR_SCALE
        ? _branchingFilter.answers?.map((a: any) => a.option)
        : _branchingFilter.answers
    }))

    dispatch(setfilters({
      surveySectionId: section.surveySectionId,
      isRepeatPerInstructor: values.isRepeatPerInstructor,
      isSelectInstructorEnabled: values.isSelectInstructorEnabled,
      attributes: preparedAttributes,
      branchingFilters: {
        filters: branchingFilters?.[0],
        parentSection,
        parentQuestion
      }
    }))
    closeFilterModal()
  }, [dispatch, section])

  useEffect(() => {
    if (section) {
      methods.setValue('isRepeatPerInstructor', section?.isRepeatPerInstructor ?? false)
      methods.setValue('isSelectInstructorEnabled', section?.isSelectInstructorEnabled ?? false)
    }
  }, [section, methods])

  return (
    <GenericModal
      title="Settings"
      showCloseIcon={false}
      action={false}
      maxWidth="lg"
      isOpen={isFilterModalOpen}
      onClose={closeFilterModal}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {!section?.parentSurveySectionId && (
            <>
              <Box display="flex" alignItems="center">
                <Controller
                  name="isRepeatPerInstructor"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={field.onChange}
                      color="primary"
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  )}
                />
                <Typography sx={{ ml: 2.5, fontWeight: 500 }}>
                  Repeat per Instructor ({isRepeatPerInstructor ? 'On' : 'Off'})
                </Typography>
              </Box>
              <Typography sx={{ ml: 13.5, color: grey[850] }}>
                Set this section to repeat for each instructor
              </Typography>

              <Box display="flex" alignItems="flex-start" mt={2}>
                <Controller
                  name="isSelectInstructorEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checkedIcon={<SelectedCheckBox width="18px" height="18px" />}
                          icon={icon}
                          checked={field.value}
                          onChange={field.onChange}
                          sx={{ ml: 1 }}
                          disabled={!isRepeatPerInstructor}
                        />
                      }
                      label=""
                    />
                  )}
                />
                <Box>
                  <Typography sx={{ ml: 0, fontWeight: 500 }}>
                    Allow users to select which instructors to evaluate
                  </Typography>
                  <Typography sx={{ ml: 0, color: grey[850] }}>
                    Before filling out this section, users will be prompted to select which instructors they want to evaluate.
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mt: 4 }} />
              <AttributesFilter attributes={section?.attributes} />
            </>
          )}

          {section?.parentSurveySectionId && <Branching section={section} />}

          <DefaultModalActions
            confirmButtonProps={{
              type: 'submit',
              text: 'Save',
              buttonType: ButtonType.Primary
            }}
            destructiveButtonProps={{
              onClick: closeFilterModal
            }}
          />
        </form>
      </FormProvider>
    </GenericModal>
  )
}
